'use client';

import { ReactNode, useState } from 'react';
import {
  HydrationBoundary,
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { toast } from 'sonner';

import { featureRefusal } from '@/shared/libs/featureRefusal';
import { retryDelayMs, shouldRetryRequest } from '@/shared/libs/retryPolicy';

/**
 * ReactQueryProvider
 *
 * A global provider that sets up React Query (TanStack Query) with sensible defaults
 * for the entire application. It handles:
 *
 * - Creating a `QueryClient` instance (with retry, staleTime, etc.)
 * - Wrapping the app with `QueryClientProvider`
 * - Adding `HydrationBoundary` for server-side rendering hydration
 * - Enabling ReactQueryDevtools for debugging (collapsed by default)
 *
 * Usage:
 * Wrap your app (e.g., in `layout.tsx`) with this provider so all components
 * can use `useQuery`, `useMutation`, etc. without additional setup.
 */
/** Dit le refus d'une rubrique fermée, une fois par rubrique. Tout le reste passe son chemin. */
function notifyFeatureRefusal(error: unknown) {
  const refusal = featureRefusal(error);
  if (!refusal) return;
  toast.error(refusal.message, { id: `feature-${refusal.feature ?? 'inconnue'}` });
}

const ReactQueryProvider = ({ children }: { children: ReactNode }) => {
  // Initialize a single QueryClient instance for the app
  const [client] = useState(
    () =>
      new QueryClient({
        /*
          **« Pas ouvert chez vous » n'est pas « pas le droit »** (#2228).

          Depuis suivora-api#1716, une route de rubrique fermée répond 403 `FEATURE_NOT_ENABLED`.
          Sans cette lecture, chaque écran affichait son erreur générique — la même que pour une
          permission manquante — et la personne partait réclamer un droit qui ne changera rien.

          Le message se dit **ici** plutôt que dans chaque écran : le refus peut tomber sur
          n'importe quelle requête, et attendre que quarante écrans le traitent chacun revient à ce
          qu'aucun ne le fasse.

          ⚠️ **Aucune redirection.** `FeatureSectionGate` porte déjà la navigation ; deux mécanismes
          qui redirigent finissent par se battre. On dit, on ne déplace pas.

          `id` porte la clé de la rubrique : un écran qui lance cinq requêtes vers une rubrique
          fermée en reçoit cinq refus, et n'affiche qu'un message.
        */
        /*
          ⚠️ **Sur les écritures seulement** (#2243). Le brancher aussi sur les lectures faisait
          parler une rubrique que personne n'avait demandée : la barre latérale interroge un
          compteur en fond, sur toutes les pages et en boucle, et le refus s'affichait à chaque
          passage — sur un tenant où la rubrique était pourtant correctement masquée. C'est le
          contraire de la règle : fermé signifie **absent**, pas signalé.

          Une lecture peut partir sans que personne n'ait rien demandé ; une écriture, non. Et pour
          la navigation, `FeatureSectionGate` a déjà son message et sa redirection.
        */
        mutationCache: new MutationCache({ onError: notifyFeatureRefusal }),
        defaultOptions: {
          queries: {
            /*
              **La reprise lit le statut** (#1682).

              C'était `retry: 2` sur tout, `retryDelay: 3000` fixe : un 403 partait trois fois, un
              429 rendait au limiteur deux appels de plus que celui qu'il venait de refuser, et un
              écran mettait neuf secondes à annoncer un échec connu dès la première réponse.
            */
            retry: shouldRetryRequest,
            retryDelay: retryDelayMs,
            staleTime: 1000 * 60 * 5, // Cache data is considered fresh for 5 minutes
            refetchOnWindowFocus: false, // Disable auto refetch when window regains focus
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={client}>
      {/* HydrationBoundary ensures SSR/SSG data can hydrate into React Query cache */}
      <HydrationBoundary state={null}>{children}</HydrationBoundary>

      {/* React Query Devtools: useful for inspecting queries/mutations in dev */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default ReactQueryProvider;
